import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  message,
  Popconfirm,
  Select,
  Space,
  Switch,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import request from "../../utils/request";
import { useGlobal } from "../../contexts/GlobalContext";
import PlanPolicySettings from "../../components/admin/PlanPolicySettings";

const { Text } = Typography;

interface URLFilterRule {
  system: string;
  query_keys?: string[];
  require_all_keys?: boolean;
  preserve_query?: boolean;
}

interface URLFilterRuleFormItem {
  system?: string;
  query_keys?: string[];
  require_all_keys?: boolean;
  preserve_query?: boolean;
}

interface URLFilterRuleFormValues {
  rules: URLFilterRuleFormItem[];
}

const defaultRuleTemplates: URLFilterRule[] = [
  { system: "Discuz", query_keys: ["mod", "tid"], require_all_keys: true },
  { system: "WordPress" },
  { system: "emlog", query_keys: ["post"], require_all_keys: true },
  { system: "typecho" },
  { system: "ZBlog", query_keys: ["id"], require_all_keys: true },
];

const normalizeRules = (rules?: URLFilterRuleFormItem[]): URLFilterRule[] =>
  (rules || [])
    .map((rule) => ({
      system: (rule.system || "").trim(),
      query_keys: (rule.query_keys || [])
        .map((key) => key.trim())
        .filter(Boolean),
      require_all_keys: !!rule.require_all_keys,
      preserve_query: !!rule.preserve_query,
    }))
    .filter((rule) => rule.system);

const SystemSettings: React.FC = () => {
  const { API_BASE_URL, token } = useGlobal();
  const [form] = Form.useForm<URLFilterRuleFormValues>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchRules = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await request.get(
        `${API_BASE_URL}/api/v3/admin/system/url-filter-rules`
      );
      if (response.data.code === 200) {
        form.setFieldsValue({ rules: response.data.data?.rules || [] });
      } else {
        message.error(response.data.msg || "获取配置失败");
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "获取配置失败");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, form, token]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleSave = async () => {
    const values = await form.validateFields();
    const rules = normalizeRules(values.rules);
    setSaving(true);
    try {
      const response = await request.put(
        `${API_BASE_URL}/api/v3/admin/system/url-filter-rules`,
        { rules }
      );
      if (response.data.code === 200) {
        message.success("全局 URL 规则已保存");
        form.setFieldsValue({ rules: response.data.data?.rules || rules });
      } else {
        message.error(response.data.msg || "保存失败");
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "保存失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: "#f0f2f5", minHeight: "100%", padding: 16 }}>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card
        title={
          <Space>
            <i className="ri-settings-4-line" style={{ color: "#1677ff", fontSize: 20 }} />
            <span>系统配置</span>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchRules} loading={loading}>
              刷新
            </Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
              保存
            </Button>
          </Space>
        }
        variant="outlined"
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <div>
            <Typography.Title level={5} style={{ marginBottom: 4 }}>
              全局 URL 归一化规则
            </Typography.Title>
          </div>

          <Form form={form} layout="vertical" initialValues={{ rules: [] }}>
            <Form.List name="rules">
              {(fields, { add, remove }) => (
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  {fields.length === 0 && (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无规则" />
                  )}
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      style={{
                        padding: 16,
                        border: "1px solid #d9d9d9",
                        borderRadius: 8,
                        background: "#fff",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          marginBottom: 12,
                        }}
                      >
                        <Text strong>{`规则 ${name + 1}`}</Text>
                        <Popconfirm title="删除这条规则？" onConfirm={() => remove(name)}>
                          <Button danger type="text" icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </div>
                      <Space direction="vertical" size={8} style={{ width: "100%" }}>
                        <Form.Item
                          {...restField}
                          name={[name, "system"]}
                          label="系统名称"
                          rules={[{ required: true, message: "请输入系统名称" }]}
                        >
                          <Input placeholder="例如 Discuz / WordPress / Halo" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, "query_keys"]}
                          label="保留的 Query Key"
                        >
                          <Select
                            mode="tags"
                            tokenSeparators={[",", " "]}
                            placeholder="例如 mod、tid；为空则移除全部 query"
                            options={[]}
                          />
                        </Form.Item>
                        <Space wrap>
                          <Form.Item
                            {...restField}
                            name={[name, "require_all_keys"]}
                            valuePropName="checked"
                            style={{ marginBottom: 0 }}
                          >
                            <Switch checkedChildren="必须齐全" unCheckedChildren="允许部分" />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, "preserve_query"]}
                            valuePropName="checked"
                            style={{ marginBottom: 0 }}
                          >
                            <Switch checkedChildren="保留全部 Query" unCheckedChildren="按 Key 过滤" />
                          </Form.Item>
                        </Space>
                      </Space>
                    </div>
                  ))}
                  <Space wrap>
                    <Button icon={<PlusOutlined />} onClick={() => add({})}>
                      添加规则
                    </Button>
                    <Button onClick={() => form.setFieldsValue({ rules: defaultRuleTemplates })}>
                      填入默认规则
                    </Button>
                  </Space>
                </Space>
              )}
            </Form.List>
          </Form>
        </Space>
      </Card>
      <PlanPolicySettings />
      </Space>
    </div>
  );
};

export default SystemSettings;
